import type { PostStatus, PostWithTargets, QueueFilters } from '@postpilot/core/domain';
import { toast } from 'sonner';
import { create } from 'zustand';
import { api, ApiError } from '@/lib/api';

export interface QueueFilterState {
  status: PostStatus[];
  accountIds: string[];
  search: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
  sortBy: QueueFilters['sortBy'];
  sortDir: QueueFilters['sortDir'];
}

const DEFAULT_FILTERS: QueueFilterState = {
  status: [],
  accountIds: [],
  search: '',
  page: 1,
  pageSize: 25,
  sortBy: 'scheduledAt',
  sortDir: 'asc',
};

interface PostsState {
  posts: PostWithTargets[];
  total: number;
  loading: boolean;
  /** True on a filter change, so we can dim the table instead of flashing a skeleton. */
  refetching: boolean;
  error: string | null;
  filters: QueueFilterState;

  fetch: () => Promise<void>;
  setFilters: (patch: Partial<QueueFilterState>) => void;
  resetFilters: () => void;

  remove: (id: string) => Promise<void>;
  retry: (id: string, accountIds?: string[]) => Promise<void>;

  /** Subscribes to live updates. Returns an unsubscribe. */
  subscribeToUpdates: (userId: string) => () => void;
}

export const usePostsStore = create<PostsState>((set, get) => ({
  posts: [],
  total: 0,
  loading: true,
  refetching: false,
  error: null,
  filters: DEFAULT_FILTERS,

  fetch: async () => {
    // First load gets a skeleton; a refetch (filter change) dims the existing
    // rows instead. Replacing a populated table with a skeleton on every
    // keystroke is far more jarring than a brief dim.
    const isFirstLoad = get().posts.length === 0;
    set(isFirstLoad ? { loading: true, error: null } : { refetching: true, error: null });

    try {
      const { filters } = get();
      const { posts, total } = await api.posts.list({
        ...filters,
        // Don't send empty arrays/strings — the API would treat `search=''` as a
        // filter matching nothing rather than as "no filter".
        status: filters.status.length ? filters.status : undefined,
        accountIds: filters.accountIds.length ? filters.accountIds : undefined,
        search: filters.search || undefined,
      });

      set({ posts, total, loading: false, refetching: false });
    } catch (error) {
      set({
        error: error instanceof ApiError ? error.message : 'Could not load the queue.',
        loading: false,
        refetching: false,
      });
    }
  },

  setFilters: (patch) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...patch,
        // Any filter change resets to page 1 — unless the caller is explicitly
        // paging. Otherwise you filter down to 3 results while on page 4 and
        // stare at an empty table.
        page: patch.page ?? 1,
      },
    }));
    void get().fetch();
  },

  resetFilters: () => {
    set({ filters: DEFAULT_FILTERS });
    void get().fetch();
  },

  remove: async (id) => {
    const previous = get().posts;

    // Optimistic removal.
    set({ posts: previous.filter((p) => p.id !== id), total: Math.max(0, get().total - 1) });

    try {
      await api.posts.remove(id);
      toast.success('Post deleted');
    } catch (error) {
      set({ posts: previous, total: previous.length });
      toast.error('Could not delete the post', {
        description: error instanceof ApiError ? error.message : 'Please try again.',
      });
    }
  },

  retry: async (id, accountIds) => {
    try {
      const updated = await api.posts.retry(id, accountIds);
      set({ posts: get().posts.map((p) => (p.id === id ? updated : p)) });
      toast.success('Re-queued', {
        description: 'The failed accounts will be retried within a minute.',
      });
    } catch (error) {
      toast.error('Could not retry', {
        description: error instanceof ApiError ? error.message : 'Please try again.',
      });
    }
  },

  /**
   * Keeps the queue fresh while it is on screen.
   *
   * The worker publishes posts in a background process the browser knows nothing
   * about. Without this, a user watching their queue would see "Scheduled"
   * forever and have to refresh to discover it went out — which makes the product
   * feel broken precisely at the moment it is working.
   *
   * With Supabase Realtime gone, we poll: a lightweight refetch on an interval,
   * paused while the tab is hidden so a backgrounded tab costs nothing. The
   * `userId` argument is kept for call-site compatibility; the API already scopes
   * every query to the caller's own rows.
   */
  subscribeToUpdates: (_userId) => {
    const POLL_MS = 8000;
    let timer: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') void get().fetch();
      }, POLL_MS);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = undefined;
    };

    // Refetch immediately when the tab comes back, so returning to it never
    // shows stale rows for up to a full interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void get().fetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    start();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisible);
    };
  },
}));

/** True when any filter is narrowing the view — drives the "Clear filters" button. */
export const selectHasActiveFilters = (state: PostsState): boolean => {
  const { status, accountIds, search, from, to } = state.filters;
  return (
    status.length > 0 || accountIds.length > 0 || Boolean(search) || Boolean(from) || Boolean(to)
  );
};
